const {
    Vector3,
    Color,
    BoxGeometry,
    MeshBasicMaterial,
    Object3D,
    Bone,
    Mesh,
    Matrix4,
    TransformControls
} = THREE;

import {
    IK,
    IKChain,
    IKJoint,
    IKRigidConstraint,
    IKBallConstraint,
    IKHingeConstraint,
    IKSliderConstraint,
    IKHelper
} from 'libs/IK';

const getModelWorldMatrix = moveAndRotateMatrixManager.getModelWorldMatrix;

Object3D.prototype.updateMatrixAndWorld = function (force) {
    this.updateMatrix();
    this.updateMatrixWorld(force);
};

class KinematicsController {
    constructor() {
        this.active = false;
        this.joints = {};
        this.jointsArray = [];
        this.solver = null;
        this.updateFunction = null;
        this.endFunction = null;
    }

    initMenu(parentMenuController, prop) {
        if (!this.menuItem) {
            const maxItemInRow = prop.maxItemInRow ? prop.maxItemInRow : 3;
            this.menuItem = new MenuData(KinematicsMenu.text, maxItemInRow, 5);
            this.menuController = new MenuController(this.menuItem);
            this.menuController.setMenuControlParameter(VR.scene, iq3Camera.camera, wv.selectionSphere);
            this.menuItem.hasMainMenuItem = true;
        }
        parseChildrenItems(this.menuController, prop.children, menuItemMaps.kinemticsItemMenuMap);

        this.menuItem.isVisible = prop.isVisible;
        this.menuItem.isPresenter = prop.isPresenter;
        this.menuItem.isViewer = prop.isViewer;
        parentMenuController.appendMenuItem(KinematicsMenu.menuItemName, prop.text || KinematicsMenu.text, this.menuItem);
    }

    async populate() {
        if (!FeatureAccessController.hasAccessInThisMeeting(FeatureEnum.KINEMATICS)) {
            return;
        }

        let data = await KinematicsService.getByMeeting();
        this.feature.populate(data);
    }

    addTarget(position = new Vector3()) {
        let self = this;
        const control = new TransformControls(iq3Camera.camera, iq3Renderer.getRenderer().domElement);
        const target = new Object3D();
        control.setSize(0.5);
        control.attach(target);
        control.target = target;
        target.position.copy(position);

        control.addEventListener("pointerdown", () => {
            VR.mouseControls.enabled = false;
        });

        control.addEventListener("pointerup", () => {
            VR.mouseControls.enabled = true;
            self.updateControlObject();
        });

        control.addEventListener("objectChange", () => {
            self.update();
        });

        VR.scene.add(control);
        VR.scene.add(target);
        target.transformControl = control;

        return target;
    }

    createJoint(chain, from, to) {
        const Z_AXES = new Vector3(0, 0, 1);
        let startPosition = getPointPositionInModelSpace(from.position, from.volumes[0], from.model);
        let endPosition = getPointPositionInModelSpace(to.position, to.volumes[0], to.model);
        let hingeAxis = new Vector3();

        let direction = new Vector3().subVectors(endPosition, startPosition);
        let length = direction.length();
        direction.normalize();

        let initialDirection = direction.clone();

        if (from.direction) {
            initialDirection.copy(from.joint._worldToLocalDirection(from.direction.clone().transformDirection(getModelWorldMatrix(getModelIdFromBranchId(from.volumes[0]))).normalize()));
        }

        if (direction.distanceTo(new Vector3(0, 1, 0)) < 1e-6 || direction.distanceTo(new Vector3(0, -1, 0)) < 1e-6) {
            from.joint._up.set(1, 0, 0);
        }

        if (chain.joints.length > 1) {
            direction = from.joint._worldToLocalDirection(direction);
        }

        let constraint;

        if (from.hinge) {
            // The first joint will always be a rigid minimal joint
            hingeAxis.copy(from.joint._worldToLocalDirection(from.hingeAxis.clone().transformDirection(getModelWorldMatrix(getModelIdFromBranchId(from.volumes[0])))));

            constraint = new IKHingeConstraint(from.angle, hingeAxis);
            from.joint._up.copy(from.hingeAxis.clone().transformDirection(getModelWorldMatrix(getModelIdFromBranchId(from.volumes[0]))));
        } else if (from.slider) {
            constraint = new IKSliderConstraint(from.sliderLength, initialDirection);
        } else if (from.angle === 0) {
            constraint = new IKRigidConstraint();
        } else {
            constraint = new IKBallConstraint(from.angle)
        }

        if (from.direction) {
            from.trueOriginalDirection = initialDirection.clone();
        }

        from.joint._worldToLocalDirection(from.joint._up);

        from.joint._originalDirection.copy(direction);

        from.trueConstraint = constraint;
        from.joint.addConstraint(new IKRigidConstraint());

        this.solver.solve();

        let endBone = new Bone();
        from.bone.add(endBone);
        endBone.position.set(0, 0, length);
        let endJoint = new IKJoint(endBone);
        endJoint._originalDirection.copy(Z_AXES);
        endBone.add(this.createBox());

        chain.add(endJoint);
        this.solver.solve();

        return endJoint;
    }

    createBox(multiplier = 1) {
        let size = 0.01 * multiplier;

        var geometry = new BoxGeometry(size, size, size);
        geometry.toNonIndexed();
        let red = new Color(1, 0, 0);
        let green = new Color(0, 1, 0);
        let blue = new Color(0, 0, 1);
        let c1 = new Color(0, 1, 1);
        let c2 = new Color(1, 0, 1);
        let c3 = new Color(1, 1, 0);
        let colors = [red, green, blue, c1, c2, c3];

        geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(24 * 3), 3));

        let colorAttribute = geometry.attributes.color;

        for (let i = 0; i < colorAttribute.array.length; i += 4) {
            let colorIndex = (i / 4) % 6;

            colorAttribute.setXYZ(i, colors[colorIndex].r, colors[colorIndex].g, colors[colorIndex].b);
            colorAttribute.setXYZ(i + 1, colors[colorIndex].r, colors[colorIndex].g, colors[colorIndex].b);
            colorAttribute.setXYZ(i + 2, colors[colorIndex].r, colors[colorIndex].g, colors[colorIndex].b);
            colorAttribute.setXYZ(i + 3, colors[colorIndex].r, colors[colorIndex].g, colors[colorIndex].b);
        }

        var material = new MeshBasicMaterial({color: 0xffffff, vertexColors: true});

        return new Mesh(geometry, material);
    }

    startProcessGeneralized() {
        let self = this;

        if (this.jointsArray.length === 0) {
            throw "Empty";
        }

        let target = this.addTarget();

        let meshes = {};

        this.solver = new IK();
        let chain = new IKChain();
        this.solver.add(chain);

        let base = this.jointsArray[0];
        let basePosition = base.position.clone().applyMatrix4(getModelWorldMatrix(base.model));
        let baseBone = new Bone();
        baseBone.position.set(0, 0, 0);
        let baseJoint = new IKJoint(baseBone);
        chain.add(baseJoint);
        baseBone.add(this.createBox());

        let pivot = new Object3D();
        pivot.position.copy(basePosition);
        pivot.add(baseBone);
        pivot.attach(target);
        pivot.attach(target.transformControl);
        VR.scene.add(pivot);

        this.jointsArray[0].joint = baseJoint;
        this.jointsArray[0].bone = baseJoint.bone;

        for (let i = 1; i < this.jointsArray.length; i++) {
            let prevData = this.jointsArray[i - 1];
            let data = this.jointsArray[i];

            let joint = this.createJoint(chain, prevData, data);

            data.joint = joint;
            data.bone = joint.bone;

            if (i === this.jointsArray.length - 1) {
                chain.setTarget(target)
            }
        }

        for (let i = 1; i < this.jointsArray.length; i += 2) {
            let data = this.jointsArray[i];
            let nextData = this.jointsArray[i + 1];
            let isHinge = data.hinge;
            let isSlider = data.slider;
            let startPosition =  getPointWorldPosition(data.position, data.volumes[0], data.model);
            let endPosition =  getPointWorldPosition(nextData.position, nextData.volumes[0], nextData.model);
            let direction = new Vector3().subVectors(endPosition, startPosition).normalize();

            if (!meshes[JSON.stringify(data.volumes)]) {
                let pivot = new Object3D();
                let mesh = new DummyMesh({
                    volumeList: data.volumes.map(branchId => modelManager.getVolume(branchId)),
                    branchIdList: data.volumes
                }, {
                    retainColor: true
                });
                wv.rootObject.add(mesh);
                mesh.visible = true;

                if (direction.distanceTo(new Vector3(0, 1, 0)) < 1e-6 || direction.distanceTo(new Vector3(0, -1, 0)) < 1e-6) {
                    pivot.up.set(1, 0, 0);
                }

                if (isHinge) {
                    pivot.up.copy(data.hingeAxis).transformDirection(getVolumeWorldMatrix(data.volumes[0]));
                }

                pivot.position.copy(startPosition);
                pivot.lookAt(endPosition);

                if (isSlider) {
                    pivot.position.copy(endPosition);
                }

                pivot.attach(mesh);

                data.joint._worldToLocalDirection(pivot.up);

                meshes[JSON.stringify(data.volumes)] = {
                    mesh: mesh,
                    pivot: pivot,
                    up: pivot.up.clone(),
                    positionIndex: isSlider ? (i + 1) : i,
                    lookIndices: [i, i + 1]
                };

                VR.scene.add(pivot);
            }
        }

        for (let data of this.jointsArray) {
            if (data.trueConstraint) {
                data.joint.constraints[0] = data.trueConstraint;
            }

            if (data.trueOriginalDirection) {
                data.joint._originalDirection.copy(data.trueOriginalDirection);
            }
        }

        let helper = new IKHelper(this.solver, {
            boneSize: 0.02,
            showAxes: false
        });

        // VR.scene.add(helper);

        let lastJoint = this.jointsArray[this.jointsArray.length - 1];
        target.position.copy(target.parent.worldToLocal(getPointWorldPosition(lastJoint.position, lastJoint.volumes[0], lastJoint.model)));

        this.updateFunction = function () {
            self.solver.solve();

            for (let key in meshes) {
                let positionIndex = meshes[key].positionIndex;
                let lookStartIndex = meshes[key].lookIndices[0];
                let lookTargetIndex = meshes[key].lookIndices[1];
                let chain = self.solver.chains[0];

                meshes[key].pivot.up.copy(meshes[key].up);
                chain.joints[lookStartIndex]._localToWorldDirection(meshes[key].pivot.up);

                chain.joints[lookStartIndex].bone.getWorldPosition(meshes[key].pivot.position);
                meshes[key].pivot.updateMatrixAndWorld();
                let lookAtTarget = new Vector3();
                chain.joints[lookTargetIndex].bone.getWorldPosition(lookAtTarget);
                meshes[key].pivot.lookAt(lookAtTarget);

                chain.joints[positionIndex].bone.getWorldPosition(meshes[key].pivot.position);
                meshes[key].pivot.updateMatrixAndWorld();
            }
        };

        this.updateControlObject = function () {
            let lastBone = self.solver.chains[0].joints[self.solver.chains[0].joints.length - 1].bone;
            recursiveUpdateMatrixWorld(lastBone);
            lastBone.getWorldPosition(target.position);
            target.parent.worldToLocal(target.position);
        };

        this.endFunction = function () {
            CatchVolumeFeatureController.temporarilySetApplyToType(CatchTargetTypeEnum.VOLUME);

            for (let key in meshes) {
                calculateMatrixForVolumeChange(meshes[key].mesh);
                meshes[key].mesh.destroy();
                meshes[key].mesh = null;
            }

            for (let joint of self.jointsArray) {
                delete joint.bone;
                delete joint.joint;
            }

            VR.scene.remove(pivot);
            VR.scene.remove(helper);
            VR.scene.remove(target);
            VR.scene.remove(target.transformControl);
            target.transformControl.enabled = false;
            self.solver = null;
            meshes = {};

            this.endFunction = null;
            this.updateFunction = null;

            CatchVolumeFeatureController.revertApplyToType();
        };

        this.currentMeshes = meshes;
    }

    startForSingleJoint(jointData) {
        let self = this;

        let jointsArray = [];

        const volume = modelManager.getVolume(jointData.volumes[0]);
        const model =  volume.modelId;
        let ground = new Vector3();

        if (jointData.pointOfAction.clone().applyMatrix4(getModelWorldMatrix(model).clone().invert()).distanceTo(ground) < 1e-6) {
            let ground = new Vector3(0, -1, 0);
        }

        jointsArray.push({
            position: ground,
            volumes: jointData.volumes,
            model: model,
            angle: 0
        });

        jointsArray.push({
            position: jointData.pointOfAction.clone().applyMatrix4(getModelWorldMatrix(model).clone().invert()),
            volumes: jointData.volumes,
            model: model,
            angle: jointData.type === "Rigid" ? 0 : jointData.angle,
            direction: jointData.initialDirection.clone(),
            hingeAxis: jointData.hingeAxis.clone(),
            hinge: jointData.type === "Hinge",
            slider: jointData.type === "Slider",
            sliderLength: jointData.sliderLength
        });

        jointsArray.push({
            position: jointData.pointOfControl.clone().applyMatrix4(getModelWorldMatrix(model).clone().invert()),
            volumes: jointData.volumes,
            model: model,
            angle: 0
        });

        let target = this.addTarget();

        let meshes = {};

        this.solver = new IK();
        let chain = new IKChain();
        this.solver.add(chain);

        let base = jointsArray[0];
        let basePosition = base.position.clone().applyMatrix4(getModelWorldMatrix(base.model));
        let baseBone = new Bone();
        baseBone.position.set(0, 0, 0);
        let baseJoint = new IKJoint(baseBone);
        chain.add(baseJoint);
        baseBone.add(this.createBox());

        let pivot = new Object3D();
        pivot.position.copy(basePosition);
        pivot.add(baseBone);
        pivot.attach(target);
        pivot.attach(target.transformControl);
        VR.scene.add(pivot);

        jointsArray[0].joint = baseJoint;
        jointsArray[0].bone = baseJoint.bone;

        for (let i = 1; i < jointsArray.length; i++) {
            let prevData = jointsArray[i - 1];
            let data = jointsArray[i];

            let joint = this.createJoint(chain, prevData, data);

            data.joint = joint;
            data.bone = joint.bone;

            if (i === jointsArray.length - 1) {
                chain.setTarget(target)
            }
        }

        for (let i = 1; i < jointsArray.length; i += 2) {
            let data = jointsArray[i];
            let nextData = jointsArray[i + 1];
            let isHinge = data.hinge;
            let isSlider = data.slider;
            let startPosition =  getPointWorldPosition(data.position, data.volumes[0], data.model);
            let endPosition =  getPointWorldPosition(nextData.position, nextData.volumes[0], nextData.model);
            let direction = new Vector3().subVectors(endPosition, startPosition).normalize();

            if (!meshes[JSON.stringify(data.volumes)]) {
                let pivot = new Object3D();
                let mesh = new DummyMesh({
                    volumeList: data.volumes.map(branchId => modelManager.getVolume(branchId)),
                    branchIdList: data.volumes
                }, {
                    retainColor: true
                });
                wv.rootObject.add(mesh);
                mesh.visible = true;

                if (direction.distanceTo(new Vector3(0, 1, 0)) < 1e-6 || direction.distanceTo(new Vector3(0, -1, 0)) < 1e-6) {
                    pivot.up.set(1, 0, 0);
                }

                if (isHinge) {
                    pivot.up.copy(data.hingeAxis).transformDirection(getVolumeWorldMatrix(data.volumes[0]));
                }

                pivot.position.copy(startPosition);
                pivot.lookAt(endPosition);

                if (isSlider) {
                    pivot.position.copy(endPosition);
                }

                pivot.attach(mesh);

                data.joint._worldToLocalDirection(pivot.up);

                meshes[JSON.stringify(data.volumes)] = {
                    mesh: mesh,
                    pivot: pivot,
                    up: pivot.up.clone(),
                    positionIndex: isSlider ? (i + 1) : i,
                    lookIndices: [i, i + 1]
                };

                VR.scene.add(pivot);
            }
        }

        for (let data of jointsArray) {
            if (data.trueConstraint) {
                data.joint.constraints[0] = data.trueConstraint;
            }

            if (data.trueOriginalDirection) {
                data.joint._originalDirection.copy(data.trueOriginalDirection);
            }
        }

        let helper = new IKHelper(this.solver, {
            boneSize: 0.02,
            showAxes: false
        });

        // VR.scene.add(helper);
        let bonePosition = new THREE.Vector3();
        self.solver.chains[0].joints[self.solver.chains[0].joints.length - 1].bone.getWorldPosition(bonePosition);
        target.position.copy(target.parent.worldToLocal(bonePosition));

        this.updateFunction = function () {
            self.solver.solve();

            for (let key in meshes) {
                let positionIndex = meshes[key].positionIndex;
                let lookStartIndex = meshes[key].lookIndices[0];
                let lookTargetIndex = meshes[key].lookIndices[1];
                let chain = self.solver.chains[0];

                meshes[key].pivot.up.copy(meshes[key].up);
                chain.joints[lookStartIndex]._localToWorldDirection(meshes[key].pivot.up);

                chain.joints[lookStartIndex].bone.getWorldPosition(meshes[key].pivot.position);
                meshes[key].pivot.updateMatrixAndWorld();
                let lookAtTarget = new Vector3();
                chain.joints[lookTargetIndex].bone.getWorldPosition(lookAtTarget);
                meshes[key].pivot.lookAt(lookAtTarget);

                chain.joints[positionIndex].bone.getWorldPosition(meshes[key].pivot.position);
                meshes[key].pivot.updateMatrixAndWorld();
            }
        };

        this.updateControlObject = function () {
            let lastBone = self.solver.chains[0].joints[self.solver.chains[0].joints.length - 1].bone;
            recursiveUpdateMatrixWorld(lastBone);
            lastBone.getWorldPosition(target.position);
            target.parent.worldToLocal(target.position);
        };

        this.endFunction = function () {
            for (let key in meshes) {
                meshes[key].mesh.parent.remove(meshes[key].mesh);
                meshes[key].mesh = null;
            }

            for (let joint of jointsArray) {
                delete joint.bone;
                delete joint.joint;
            }

            VR.scene.remove(pivot);
            VR.scene.remove(helper);
            VR.scene.remove(target);
            VR.scene.remove(target.transformControl);
            target.transformControl.enabled = false;
            self.solver = null;
            meshes = {};

            this.endFunction = null;
            this.updateFunction = null;
        };

        this.currentMeshes = meshes;
    }

    startProcess() {
        if (this.active) {
            return;
        }

        wv.onDragControlChange(false);
        clearAllFeaturesFromAllHandler();

        this.active = true;
        try {
            this.startProcessGeneralized();
        } catch (e) {
            this.active = false;
            console.error(e);
        }

        menuViewToolbarController.setToolbarItemStatusById("kinematicsStart", instance.active, instance.active ? "Stop Kinematics" : "Start Kinematics");
    }

    update() {
        if (this.updateFunction) {
            this.updateFunction();
        }
    }

    endProcess() {
        if (!this.active) {
            return;
        }

        this.active = false;
        if (this.endFunction) {
            this.endFunction();
        }

        menuViewToolbarController.setToolbarItemStatusById("kinematicsStart", instance.active, instance.active ? "Stop Kinematics" : "Start Kinematics");
    }

    parseJoint(jointData) {
        let model =  getModelIdFromBranchId(jointData.volumes[0]);

        if (this.jointsArray.length === 0) {
            let ground = new Vector3();

            if (jointData.pointOfAction.clone().applyMatrix4(jointData.currentPointMatrix).distanceTo(ground) < 1e-6) {
                let ground = new Vector3(0, -1, 0);
            }

            this.jointsArray.push({
                position: ground,
                volumes: jointData.volumes,
                model: model,
                angle: 0
            })
        }

        this.jointsArray.push({
            position: jointData.pointOfAction.clone().applyMatrix4(jointData.currentPointMatrix),
            volumes: jointData.volumes,
            model: model,
            angle: jointData.type === "Rigid" ? 0 : jointData.angle,
            direction: jointData.initialDirection.clone().transformDirection(jointData.currentPointMatrix),
            hingeAxis: jointData.hingeAxis.clone().transformDirection(jointData.currentPointMatrix),
            hinge: jointData.type === "Hinge",
            slider: jointData.type === "Slider",
            sliderLength: jointData.sliderLength
        });

        this.jointsArray.push({
            position: jointData.pointOfControl.clone().applyMatrix4(jointData.currentPointMatrix),
            volumes: jointData.volumes,
            model: model,
            angle: 0
        });
    }

    parseNodes(nodes) {
        let ground = new Vector3();

        if (nodes[0].children[0].pointOfAction.clone().applyMatrix4(nodes[0].children[0].currentPointMatrix).distanceTo(ground) < 1e-6) {
            ground = new Vector3(0, -1, 0);
        }

        this.jointsArray.push({
            position: ground,
            volumes: nodes[0].children[0].volumes,
            model: getModelIdFromBranchId(nodes[0].children[0].volumes[0]),
            angle: 0
        });

        for (let node of nodes) {
            let joint = node.children[0];

            if (!joint) {
                continue;
            }

            let model = getModelIdFromBranchId(joint.volumes[0]);

            this.jointsArray.push({
                position: joint.pointOfAction.clone().applyMatrix4(joint.currentPointMatrix),
                volumes: joint.volumes,
                model: model,
                angle: joint.type === "Rigid" ? 0 : joint.angle,
                direction: joint.initialDirection.clone().transformDirection(joint.currentPointMatrix),
                hingeAxis: joint.hingeAxis.clone().transformDirection(joint.currentPointMatrix),
                hinge: joint.type === "Hinge",
                slider: joint.type === "Slider",
                sliderLength: joint.sliderLength
            });

            this.jointsArray.push({
                position: joint.pointOfControl.clone().applyMatrix4(joint.currentPointMatrix),
                volumes: joint.volumes,
                model: model,
                angle: 0
            });
        }
    }

    registerFeature(feature) {
        this.feature = feature;
    }

    showChainList() {
        this.feature.showChainList();
    }
}

const instance = new KinematicsController();

document.addEventListener('kinematics.toggleState', function () {
    if (!instance.active) {
        instance.startProcess();
    } else {
        instance.endProcess();
    }
});

document.addEventListener('kinematics.chain.list.open', function () {
    instance.showChainList();
});

export default instance;



